<template>
  <div>
    <Heading v-if="title" level="h6" class="mb-3 !mt-0">{{ title }}</Heading>
    <div v-if="hasData" :style="{ height: `${height}px` }">
      <canvas ref="chartCanvas"></canvas>
    </div>
    <EmptyState 
      v-else
      icon="chart" 
      :message="emptyMessage" 
      :height="`h-[${height}px]`"
      :show-action="showAction"
      :action-text="actionText"
      :action-icon="actionIcon"
      :action-variant="actionVariant"
      :action-size="actionSize"
      @action="handleAction"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue'
import { Chart, BarController, BarElement, LinearScale, CategoryScale, Title, Tooltip, Legend } from 'chart.js'
import Heading from '@/components/ui/Heading.vue'
import EmptyState from '@/components/ui/EmptyState.vue'
import { t } from '@/i18n/en-us'

Chart.register(BarController, BarElement, LinearScale, CategoryScale, Title, Tooltip, Legend)

const props = defineProps({
  data: {
    type: Array,
    required: true
  },
  height: {
    type: Number,
    default: 300
  },
  title: {
    type: String,
    default: ''
  },
  emptyMessage: {
    type: String,
    default: () => t('reports.noDataAvailable')
  },
  showAction: {
    type: Boolean,
    default: false
  },
  actionText: {
    type: String,
    default: ''
  },
  actionIcon: {
    type: String,
    default: 'plus'
  },
  actionVariant: {
    type: String,
    default: 'primary'
  },
  actionSize: {
    type: String,
    default: 'sm'
  }
})

const emit = defineEmits(['action'])

const chartCanvas = ref(null)
let chartInstance = null

// Computed
const hasData = computed(() => props.data && props.data.length > 0)

const createChart = () => {
  if (!chartCanvas.value || !props.data || props.data.length === 0) return

  const ctx = chartCanvas.value.getContext('2d')
  
  if (chartInstance) {
    chartInstance.destroy()
  }

  // Extract colors from data if available, otherwise use defaults
  const colors = props.data.map(d => d.color).filter(Boolean)
  const defaultColors = [
    'rgba(59, 130, 246, 0.8)',
    'rgba(16, 185, 129, 0.8)',
    'rgba(249, 115, 22, 0.8)',
    'rgba(239, 68, 68, 0.8)',
    'rgba(168, 85, 247, 0.8)'
  ]
  const backgroundColors = colors.length > 0 ? colors : defaultColors

  // Custom plugin to display data labels on bars
  const dataLabelsPlugin = {
    id: 'customDataLabels',
    afterDatasetsDraw: (chart) => {
      const ctx = chart.ctx
      chart.data.datasets.forEach((dataset, datasetIndex) => {
        const meta = chart.getDatasetMeta(datasetIndex)
        meta.data.forEach((bar, index) => {
          const value = dataset.data[index]
          
          // Set label style
          ctx.fillStyle = '#374151' // gray-700
          ctx.font = 'bold 12px sans-serif'
          ctx.textAlign = 'left'
          ctx.textBaseline = 'middle'
          
          // Position the label at the end of the bar
          const text = value.toLocaleString()
          const padding = 8
          const x = bar.x + padding
          const y = bar.y
          
          ctx.fillText(text, x, y)
        })
      })
    }
  }

  chartInstance = new Chart(ctx, {
    type: 'bar',
    data: {
      labels: props.data.map(d => d.label),
      datasets: [{
        label: 'Count',
        data: props.data.map(d => d.value),
        backgroundColor: backgroundColors
      }]
    },
    options: {
      indexAxis: 'y', // This makes it horizontal
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: {
          right: 60 // Extra space for labels
        }
      },
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          callbacks: {
            label: function(context) {
              return `Count: ${context.parsed.x.toLocaleString()}`
            }
          }
        }
      },
      scales: {
        x: {
          beginAtZero: true,
          ticks: {
            precision: 0
          }
        },
        y: {
          ticks: {
            font: {
              size: 13,
              weight: '500'
            }
          }
        }
      }
    },
    plugins: [dataLabelsPlugin]
  })
}

const handleAction = () => {
  emit('action')
}

onMounted(() => {
  createChart()
})

watch(() => props.data, async () => {
  // Wait for DOM to update (canvas to be re-mounted if it was removed)
  await nextTick()
  createChart()
}, { deep: true })
</script>

